/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Cashback Reserve Modal container component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.12.0
 */

import { useCallback, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { pushGTMAnalytic } from '@modules/analytics/GoogleTagManager/GTMActions';
import type { IGTMPushAnalyticPayload } from '@modules/analytics/GoogleTagManager/types/GTMActionTypes';
import type { ICommonTranslation } from '@modules/internationalization/types';
import type { IPageCashbackReserveModalTranslation } from '@modules/internationalization/types/pages/cashback';
import Modal from '@modules/modal/components/Modal';
import { EModalStatus, IModalRenderProps } from '@modules/modal/types/modalTypes';
import { cashbackReserveStartAction } from '@pages/PageCashback/actions/cashbackControlActions';
import type { IPageCashbackReducer } from '@pages/PageCashback/pageCashbackReducer';
import { makeSelectCashbackReserveError } from '@pages/PageCashback/selectors/cashbackControlSelectors';
import type { ICashbackControlConnectedService, ICashbackReserveStartPayload } from '@pages/PageCashback/types/cashbackControlTypes';
import type { IRootReducer } from '@reducers/rootReducer';

import CashbackReserveModalFailure from './CashbackReserveModalFailure';
import CashbackReserveModalStart from './CashbackReserveModalStart';
import CashbackReserveModalSuccess from './CashbackReserveModalSuccess';

export interface ICashbackReserveModal extends Pick<IModalRenderProps, 'onCloseModal'> {
    maxScore: number;
    service: ICashbackControlConnectedService;
    translation: IPageCashbackReserveModalTranslation;
    translationCommon: ICommonTranslation;
}

const CashbackReserveModal = (props: ICashbackReserveModal & IConnectedProps & IDispatchProps) => {

    const {
        reserveError,
        service,
        translation,
        translationCommon,

        onCloseModal,
        pushGtmAnalytic,
        reserveStart,
    } = props;

    const [scoreReserved, setReservedScore] = useState<number>(0);
    const [isReserveAllChecked, setReserveAllChecked] = useState<boolean>(false);

    /*
    * -------------------------------------------
    *         Render
    * -------------------------------------------
    */

    const onReserve = useCallback((score: number, isReserveAll: boolean) => {
        reserveStart({ loyaltyRewardId: service.loyaltyRewardId, amount: score });
        setReservedScore(score);
        setReserveAllChecked(isReserveAll);
    }, [service, reserveStart]);


    return (

        <Modal dataName="cashback-info-modal" width="580px">

            <Modal.Status status={EModalStatus.start}>
                <CashbackReserveModalStart {...props} reserve={onReserve} />
            </Modal.Status>

            <Modal.Status status={EModalStatus.failure}>
                <CashbackReserveModalFailure
                    {...props}
                    pushGtmAnalytic={pushGtmAnalytic}
                    reserveError={reserveError}
                />
            </Modal.Status>

            <Modal.Status status={EModalStatus.success}>
                <CashbackReserveModalSuccess
                    isReserveAllChecked={isReserveAllChecked}
                    pushGtmAnalytic={pushGtmAnalytic}
                    scoreReserved={scoreReserved}
                    service={service}
                    translation={translation}
                    translationCommon={translationCommon}
                    onCloseModal={onCloseModal}
                />
            </Modal.Status>
        </Modal>
    );
};

export type IConnectedProps = Pick<IPageCashbackReducer, 'reserveError'>;

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    reserveError: makeSelectCashbackReserveError(),
});

export interface IDispatchProps {
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    reserveStart: (payload: ICashbackReserveStartPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
    reserveStart: (payload: ICashbackReserveStartPayload) => dispatch(cashbackReserveStartAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CashbackReserveModal);
